import { useReducer } from "react";

import {
  STAFF_BOOKING_STEPS,
  type StaffBookingData,
  type StaffBookingStep,
} from "./staff-booking-types";

interface StaffBookingState {
  step: StaffBookingStep;
  history: StaffBookingStep[];
  data: StaffBookingData;
}

type StaffBookingAction =
  | {
      type: "next";
      step: StaffBookingStep;
      patch?: Partial<StaffBookingData>;
    }
  | { type: "back" }
  | { type: "update"; patch: Partial<StaffBookingData> };

const staffBookingReducer = (
  state: StaffBookingState,
  action: StaffBookingAction,
): StaffBookingState => {
  switch (action.type) {
    case "next":
      if (action.step === state.step) return state;

      return {
        step: action.step,
        history: [...state.history, state.step],
        data: { ...state.data, ...action.patch },
      };
    case "back": {
      const previousStep = state.history.at(-1);

      if (!previousStep) return state;

      return {
        ...state,
        step: previousStep,
        history: state.history.slice(0, -1),
      };
    }
    case "update":
      return { ...state, data: { ...state.data, ...action.patch } };
  }
};

export const useStaffBookingSteps = (userId: string | null) => {
  const [state, dispatch] = useReducer(staffBookingReducer, {
    step: userId ? STAFF_BOOKING_STEPS.VISIT_TYPE : STAFF_BOOKING_STEPS.EMAIL,
    history: [],
    data: {
      email: "",
      visitType: null,
      stateId: null,
      insuranceId: null,
      providerId: null,
      appointmentDetails: null,
      patientInfo: null,
    },
  });

  return {
    step: state.step,
    data: state.data,
    next: (step: StaffBookingStep, patch?: Partial<StaffBookingData>) =>
      dispatch({ type: "next", step, patch }),
    back: () => dispatch({ type: "back" }),
    update: (patch: Partial<StaffBookingData>) =>
      dispatch({ type: "update", patch }),
  };
};
